// Surat Jalan: the list, its filters, the note's page and its cards, the printed document, the public verification.
// Formal and calm, no exclamation marks, no "…" in action labels; "Titik Distribusi" and "Reader" in every sentence.
import type { ShipmentKind } from '@lib/api/common';
import type { ItemOutcomeFilter, ShipmentItemOutcome, ShipmentSide, ShipmentTab } from '@lib/api/shipments';

export const SHIPMENTS = {
  title: 'Surat Jalan',
  sub: 'Surat Jalan distribusi dan penarikan tabung, dari dimuat sampai diterima.',
  noun: 'Surat Jalan',
  loading: 'Memuat Surat Jalan…',
  results: 'Daftar Surat Jalan',
} as const;

/** The status tabs; their labels are also the row badges (one word for one state across the page). */
export const SHIPMENT_TABS: Record<ShipmentTab, string> & { label: string; name: (label: string, n: string) => string } = {
  label: 'Status Surat Jalan',
  all: 'Semua',
  loading: 'Dimuat',
  in_transit: 'Dalam pengiriman',
  late: 'Terlambat',
  discrepancy: 'Ada selisih',
  done: 'Selesai',
  cancelled: 'Dibatalkan',
  name: (label, n) => `${label}, ${n} Surat Jalan`,
};

/** What each kind of note is for (the kind chip's tooltip). */
export const SHIPMENT_KIND_TITLE: Record<ShipmentKind, string> = {
  DISTRIBUTION: 'Surat Jalan distribusi: Gudang Pusat, SPBG, dan Hub',
  WITHDRAWAL: 'Surat Jalan penarikan: tabung dibawa ke Bengkel Uji untuk uji berkala atau karena rusak',
};

/** The row and header badge of a note, by what it is now. */
export const SHIPMENT_BADGE = {
  loading: 'Dimuat',
  inTransit: 'Dalam pengiriman',
  late: 'Terlambat',
  unloading: 'Sedang dibongkar',
  received: 'Diterima',
  discrepancy: 'Ada selisih',
  cancelled: 'Dibatalkan',
  draft: 'Draf',
} as const;

export const SHIPMENT_FILTERS = {
  search: 'Cari Surat Jalan',
  searchPlaceholder: 'Cari nomor Surat Jalan atau plat',
  searchPlaceholderShort: 'Nomor atau plat',
  region: 'Wilayah',
  place: 'Titik Distribusi',
  side: 'Sebagai',
  sides: { both: 'Keduanya', origin: 'Asal', destination: 'Tujuan' } as Record<ShipmentSide, string>,
  /** the chip of a chosen place: which end it must be */
  placeChip: (side: ShipmentSide, name: string) => (side === 'both' ? name : `${side === 'origin' ? 'Asal' : 'Tujuan'}: ${name}`),
  placeSearch: 'Cari Titik Distribusi',
  allPlaces: 'Semua Titik Distribusi',
  placesEmpty: 'Tidak ada Titik Distribusi yang cocok',
  placesFailed: 'Daftar Titik Distribusi gagal dimuat.',
  morePlaces: (n: number) => `${n} teratas. Ketik nama untuk mencari yang lain.`,
  searching: 'Mencari…',
  somePlace: 'Titik Distribusi terpilih',
  kind: 'Jenis',
  allKinds: 'Semua jenis',
  /** under each kind in the menu: what it carries */
  kindSub: { DISTRIBUTION: 'Gudang Pusat, SPBG, dan Hub', WITHDRAWAL: 'Ke Bengkel Uji untuk uji berkala atau karena rusak' },
  date: 'Tanggal',
  /** what the date filter means, under the calendar */
  dateHint: 'Menurut tanggal Surat Jalan dibuat (WIB).',
} as const;

export const SHIPMENT_COLUMNS = {
  number: 'Surat Jalan',
  route: 'Rute',
  status: 'Status',
  cylinders: 'Tabung',
  time: 'Waktu',
} as const;

export const SHIPMENT_ROW = {
  menu: (number: string) => `Tindakan untuk ${number}`,
  open: 'Lihat rincian',
  print: 'Cetak',
  map: 'Lihat di peta',
  loaded: (n: string) => `${n} dimuat`,
  received: (got: string, of: string) => `${got} / ${of} diterima`,
  selisih: (n: string) => `Selisih ${n}`,
  /** the estimate under an in-transit badge */
  eta: (at: string) => `tiba ±${at}`,
  late: (at: string) => `perkiraan tiba ${at}`,
  progress: (pct: string) => `${pct} perjalanan`,
  progressName: (pct: string, at: string) => `Perkiraan ${pct} perjalanan, tiba sekitar ${at}`,
  timeOf: { created: 'dibuat', departed: 'berangkat', received: 'diterima', cancelled: 'dibatalkan' },
} as const;

export const SHIPMENT_EMPTY = {
  none: {
    title: 'Belum ada Surat Jalan',
    text: 'Surat Jalan dibuat operator di Operasional saat tabung dimuat, lalu tampil di sini.',
  },
  noMatch: 'Tidak ada Surat Jalan yang cocok. Ubah kata kunci atau filter.',
  /** the tabs' counts did not load (the list itself may have) */
  countsFailed: 'Jumlah per status belum dapat dimuat.',
  noMatchSearch: 'Tidak ada Surat Jalan yang cocok. Ketik awal nomor Surat Jalan (SJ/2026/10/0001) atau plat kendaraan.',
  tab: {
    all: 'Belum ada Surat Jalan.',
    loading: 'Tidak ada Surat Jalan yang sedang dimuat.',
    in_transit: 'Tidak ada Surat Jalan dalam pengiriman.',
    late: 'Tidak ada Surat Jalan yang melewati perkiraan tiba.',
    discrepancy: 'Tidak ada Surat Jalan dengan selisih.',
    done: 'Belum ada Surat Jalan yang selesai.',
    cancelled: 'Tidak ada Surat Jalan yang dibatalkan.',
  } as Record<ShipmentTab, string>,
} as const;

/** The note's own page. */
export const SHIPMENT_DETAIL = {
  missing: 'Surat Jalan tidak ditemukan',
  back: 'Kembali ke daftar Surat Jalan',
  print: 'Cetak',
  map: 'Lihat di peta',
  more: 'Tindakan lain',
  continueOps: 'Lanjutkan di Operasional',
  /** the route sentence under the number */
  route: (from: string, to: string) => `Dari ${from} ke ${to}`,
  demo: 'Data simulasi: Surat Jalan ini dibuat untuk demo dan tidak mewakili kiriman nyata.',
  seeAll: (n: string) => `Lihat semua (${n})`,
  seeAllPlain: 'Lihat semua',
  banner: {
    loading: (place: string, n: string) => `Sedang dimuat di ${place}: ${n} tabung di daftar muat. Nomor Surat Jalan terbit saat truk diberangkatkan.`,
    loadingPaused: (place: string, n: string) => `Pemuatan di ${place} dijeda: ${n} tabung di daftar muat. Nomor Surat Jalan terbit saat truk diberangkatkan.`,
    cancelled: (at: string, place: string) => `Surat Jalan ini dibatalkan ${at}. Tabung yang sempat dimuat tetap tercatat di ${place}.`,
  },
} as const;

/** Dimuat → Berangkat → Diterima. */
export const SHIPMENT_STEPS = {
  title: 'Perjalanan',
  label: 'Tahapan Surat Jalan',
  loadingPaused: 'Pemuatan dijeda',
  lateNoEta: 'Melewati perkiraan tiba',
  discrepancy: (n: string) => `${n} tabung tidak sesuai`,
  loaded: 'Dimuat',
  departed: 'Berangkat',
  received: 'Diterima',
  receivedDiscrepancy: 'Diterima dengan selisih',
  cancelled: 'Dibatalkan',
  pending: 'Belum',
  loadingNow: 'Sedang dimuat',
  unloadingNow: 'Sedang dibongkar',
  onTheWay: 'Dalam pengiriman',
  byLead: 'oleh',
  cylinders: (n: string) => `${n} tabung`,
  done: 'selesai',
  current: 'sedang berjalan',
  upcoming: 'belum',
} as const;

export const SHIPMENT_TRANSIT = {
  title: 'Dalam pengiriman',
  mapLabel: (number: string) => `Peta rute Surat Jalan ${number}`,
  progress: 'Perkiraan perjalanan',
  progressName: (pct: string) => `Perkiraan ${pct} perjalanan`,
  eta: (at: string) => `Perkiraan tiba ${at}`,
  late: (at: string) => `Melewati perkiraan tiba (${at})`,
  noEta: 'Perkiraan tiba belum tersedia untuk rute ini.',
  basis: 'Perkiraan dari lama perjalanan biasa di rute ini; tanpa GPS.',
  departed: (at: string) => `Berangkat ${at}`,
  unloading: (place: string, got: string, of: string) => `Sedang dibongkar di ${place}: ${got} dari ${of} tabung cocok.`,
  unloadingPaused: (place: string, got: string, of: string) => `Pembongkaran di ${place} dijeda: ${got} dari ${of} tabung cocok.`,
  notYet: (n: string) => `${n} tabung belum terbaca`,
  noPoint: 'Titik Distribusi asal atau tujuan belum memiliki titik di peta.',
} as const;

export const SHIPMENT_SUMMARY = {
  title: 'Ringkasan',
  kind: 'Jenis',
  /** a withdrawal's reason for its cylinders unless one says otherwise */
  defaultReason: (reason: string) => `alasan utama: ${reason}`,
  vehicle: 'Kendaraan',
  driver: 'Sopir',
  loadedBy: 'Petugas muat',
  departedBy: 'Diberangkatkan oleh',
  receivedBy: 'Petugas penerima',
  readers: 'Reader',
  readerSide: { load: 'muat', unload: 'bongkar' },
  noReader: 'Belum ada pembacaan Reader',
  created: 'Dibuat',
  origin: 'Asal',
  destination: 'Tujuan',
  none: '–',
} as const;

export const ITEM_OUTCOME: Record<ShipmentItemOutcome, string> & { open: string } = {
  RECEIVED: 'Diterima',
  MISSING: 'Tidak sampai',
  open: 'Dimuat',
};
/** The manifest drawer's filter. */
export const ITEM_FILTER: Record<ItemOutcomeFilter, string> = { RECEIVED: 'Diterima', MISSING: 'Tidak sampai', PENDING: 'Belum diterima' };

export const SHIPMENT_ITEMS = {
  title: 'Isi muatan',
  drawerTitle: (number: string) => `Isi muatan ${number}`,
  noun: 'tabung',
  filter: 'Hasil',
  all: 'Semua',
  empty: 'Belum ada tabung dimuat.',
  emptyCancelled: 'Tabung yang sempat dimuat dikeluarkan saat Surat Jalan dibatalkan.',
  emptyOutcome: 'Tidak ada tabung dengan hasil ini.',
  loadedAt: (at: string) => `dimuat ${at}`,
  receivedAt: (at: string) => `diterima ${at}`,
  manual: 'manual',
  /** a withdrawn cylinder's reason */
  reason: (reason: string) => `Alasan: ${reason}`,
  counts: { loaded: 'Dimuat', received: 'Diterima', missing: 'Tidak sampai', rejected: 'Ditolak' },
  /** the counts are the note's own once it is closed */
  frozen: 'Jumlah tercatat saat Surat Jalan ditutup.',
} as const;

export const SHIPMENT_DISCREPANCY = {
  title: 'Selisih',
  hint: 'Yang tidak sesuai antara dimuat dan dibongkar.',
  missing: (n: string) => `${n} tabung tidak sampai`,
  missingWhy: (origin: string) => `Terbaca saat dimuat di ${origin}, tidak terbaca saat dibongkar.`,
  rejected: (n: string) => `${n} tag ditolak saat dimuat`,
  rejectedWhy: 'Terbaca di Reader pengirim tetapi tidak boleh masuk Surat Jalan.',
  more: (n: string) => `dan ${n} lainnya`,
  extras: (n: string) => `${n} tag di luar kiriman`,
  extrasWhy: 'Terbaca saat dibongkar tetapi bukan bagian Surat Jalan ini. Tidak ikut diterima.',
  note: 'Catatan penerima',
  lastSeen: (place: string, at: string) => `terakhir terbaca di ${place}, ${at}`,
  neverSeen: 'belum terbaca lagi',
  found: 'Sudah ditemukan',
  lost: 'Dilaporkan hilang',
  seenAgain: (at: string, place: string) => `Terbaca lagi ${at} di ${place}.`,
  notSeenAgain: 'Belum terbaca lagi. “Tandai ditemukan” aktif setelah tabung terbaca oleh Reader mana pun.',
  unknownTag: 'Tag belum terdaftar',
  recordedAt: (place: string) => `Tercatat di ${place}`,
  elsewhere: 'Titik Distribusi lain',
} as const;

export const SHIPMENT_REJECTIONS = {
  title: 'Ditolak saat dimuat',
  hint: 'Terbaca di Reader pengirim tetapi tidak boleh masuk Surat Jalan.',
  drawerTitle: (number: string) => `Ditolak saat dimuat · ${number}`,
} as const;

export const SHIPMENT_ALERTS = {
  title: 'Peringatan terkait',
  empty: 'Tidak ada peringatan untuk Surat Jalan ini.',
  resolved: 'Selesai',
} as const;

export const SHIPMENT_MAP = {
  title: (number: string) => `Rute ${number}`,
  departed: 'Berangkat',
  eta: 'Perkiraan tiba',
  legend: 'Posisi truk adalah perkiraan dari waktu berangkat, bukan GPS.',
} as const;

export const SHIPMENT_CANCEL = {
  label: 'Batalkan Surat Jalan',
  title: 'Batalkan Surat Jalan ini?',
  body: (place: string) => `Pemuatan dihentikan dan draf ditutup. Tabung yang sempat dimuat tetap tercatat di ${place}. Tindakan ini tidak dapat diurungkan.`,
  confirm: 'Batalkan Surat Jalan',
  done: 'Surat Jalan dibatalkan',
} as const;

/** The printed document and its toolbar. */
export const SHIPMENT_PRINT = {
  back: 'Kembali ke Surat Jalan',
  print: 'Cetak',
  preparing: 'Menyiapkan dokumen…',
  failed: 'Dokumen tidak dapat dimuat.',
  pdfHint: 'Untuk PDF, pilih “Simpan sebagai PDF” di jendela cetak.',
  masked: 'Nama sopir dan petugas disamarkan untuk akun Anda.',
  size: 'Ukuran kertas',
  sizes: { a5: 'A5 mendatar', form: 'Formulir sambung 9½ × 11 inci' },
  sizeHint: { a5: 'Setengah A4: printer laser kantor mana pun.', form: 'Kertas sambung untuk printer dot-matrix.' },
  copy: 'Lembar untuk',
  copies: { all: 'Semua lembar (3)', sender: 'Pengirim', driver: 'Sopir', receiver: 'Penerima' },
  copyMark: (who: string) => `Lembar untuk ${who}`,
  docTitle: 'SURAT JALAN',
  /** the title with the note's kind */
  docTitleOf: { DISTRIBUTION: 'SURAT JALAN DISTRIBUSI', WITHDRAWAL: 'SURAT JALAN PENARIKAN' } as Record<ShipmentKind, string>,
  /** a withdrawn cylinder's reason (its own column on a withdrawal) */
  reason: 'Alasan',
  demo: 'SIMULASI',
  draft: 'DRAF · belum berangkat',
  date: 'Tanggal',
  departed: 'Berangkat',
  received: 'Diterima',
  origin: 'Asal',
  destination: 'Tujuan',
  vehicle: 'Kendaraan',
  driver: 'Sopir',
  cylinders: 'Jumlah tabung',
  cylindersValue: (n: string) => `${n} tabung`,
  summary: (loaded: string, got: string, missing: string) => `Dimuat ${loaded} · Diterima ${got} · Tidak sampai ${missing}`,
  no: 'No',
  serial: 'Nomor seri',
  remark: 'Ket.',
  remarkOf: { RECEIVED: 'Diterima', MISSING: 'Tidak sampai' },
  noItems: 'Belum ada tabung dimuat.',
  truncated: (shown: string, total: string) => `Daftar terpotong: ${shown} dari ${total} tabung tercantum. Daftar lengkap ada di sistem.`,
  note: 'Catatan penerima',
  signs: { sender: 'Pengirim', driver: 'Sopir', receiver: 'Penerima' },
  signedDigital: (at: string) => `Tercatat digital ${at}`,
  signName: '(nama jelas)',
  verify: 'Pindai untuk memeriksa keaslian',
  verifyQr: (number: string) => `Kode QR verifikasi Surat Jalan ${number}`,
  orgFallback: 'Pemilik tabung',
  /** the PDF's file name */
  fileTitle: (number: string) => `Surat Jalan ${number.replace(/\//g, '-')}`,
} as const;

/** /v/sj/:token, opened from the printed QR by anyone (no sign-in). */
export const SHIPMENT_VERIFY = {
  title: 'Verifikasi Surat Jalan',
  loading: 'Memeriksa dokumen…',
  valid: 'Dokumen ini terdaftar di sistem',
  validSub: 'Data di bawah adalah catatan sistem untuk Surat Jalan ini. Cocokkan dengan dokumen yang Anda pegang.',
  cancelled: 'Surat Jalan ini dibatalkan dan tidak berlaku untuk pengiriman.',
  demoSub: 'Data simulasi untuk demo sistem. Dokumen ini tidak berlaku untuk pengiriman.',
  notFound: 'Dokumen tidak ditemukan',
  notFoundSub: 'Kode pada dokumen ini tidak cocok dengan Surat Jalan mana pun di sistem. Periksa kembali dokumen, atau hubungi pengirim.',
  failed: 'Pemeriksaan gagal. Coba lagi sebentar lagi.',
  offline: 'Tidak ada koneksi internet. Sambungkan perangkat, lalu coba lagi.',
  number: 'Nomor',
  status: 'Status',
  route: 'Rute',
  departed: 'Berangkat',
  received: 'Diterima',
  cylinders: 'Jumlah tabung',
  notYet: 'Belum',
  checkedAt: (at: string) => `Diperiksa ${at}`,
  privacy: 'Halaman ini hanya menampilkan data umum Surat Jalan, tanpa data pribadi.',
  demo: 'Data simulasi',
} as const;
